/**
 * AI Chat Panel Component
 * The main chat interface for interacting with the AI assistant.
 */

import { useState, useEffect, useRef, useCallback } from 'react';
import { History, X, RefreshCw, Info } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { ScrollArea } from '@/components/ui/scroll-area';
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from '@/components/ui/tooltip';
import { cn } from '@/lib/utils';
import { toast } from '@/hooks/use-toast';
import { useAuth } from '@/hooks/useAuth';
import { AIMessageItem } from './AIMessage';
import { AIChatInput } from './AIChatInput';
import { AIConversationList } from './AIConversationList';
import {
  sendChatMessage,
  streamChatMessage,
  uploadAIAttachment,
  getConversations,
  getConversationMessages,
  archiveConversation,
} from '@/services/ai-assistant';
import type { AIAttachment, AIChatResponse } from '@/types/ai-assistant';
import type { AIMessage, AIConversation } from '@/types/ai-assistant';

interface AIChatPanelProps {
  isOpen: boolean;
  onClose: () => void;
  /** 'drawer' (default) renders the fixed right-side panel; 'page' fills its parent. */
  variant?: 'drawer' | 'page';
}

/** Friendly labels for tool activity, shown in the status line. */
const TOOL_LABELS: Record<string, string> = {
  search_sharepoint: 'Searching SharePoint',
  read_sharepoint_file: 'Reading a SharePoint file',
  get_timesheet_summary: 'Summarizing timesheets',
  analyze_timesheets: 'Reading timesheets with the fast model',
  create_pdf_report: 'Generating the PDF report',
  get_statistics: 'Crunching statistics',
  list_audit_logs: 'Searching the audit trail',
  list_matter_phases: 'Fetching matter phases',
};

function toolActivityLabel(tool: string, detail?: string): string {
  const label =
    TOOL_LABELS[tool] ||
    (tool.startsWith('list_') || tool.startsWith('get_')
      ? `Fetching ${tool.replace(/^(list|get)_/, '').replace(/_/g, ' ')}`
      : `Using ${tool.replace(/_/g, ' ')}`);
  // detail is a compact JSON of the tool input, e.g. {"search": "BDO"}
  const hint = detail && detail !== '{}' ? ` ${detail.slice(0, 60)}` : '';
  return `${label}${hint}…`;
}

const WELCOME_SUGGESTIONS = [
  "Show me my active matters",
  "What clients do I have?",
  "Create a timesheet for 2 hours of research",
  "How many hours did I bill this week?",
  "Show my billing statistics for this month",
];

export function AIChatPanel({ isOpen, onClose, variant = 'drawer' }: AIChatPanelProps) {
  const { user } = useAuth();
  const [messages, setMessages] = useState<AIMessage[]>([]);
  const [conversations, setConversations] = useState<AIConversation[]>([]);
  const [activeConversationId, setActiveConversationId] = useState<number | null>(null);
  const [inputValue, setInputValue] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  // What the assistant is doing right now ("Thinking…", "Using X…"); null while text streams
  const [activity, setActivity] = useState<string | null>(null);
  const [attachments, setAttachments] = useState<AIAttachment[]>([]);
  const [isUploading, setIsUploading] = useState(false);
  const [isLoadingConversations, setIsLoadingConversations] = useState(false);
  const [showHistory, setShowHistory] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const messagesEndRef = useRef<HTMLDivElement>(null);
  const scrollAreaRef = useRef<HTMLDivElement>(null);
  
  // Scroll to bottom when new messages arrive
  const scrollToBottom = useCallback(() => {
    if (messagesEndRef.current) {
      messagesEndRef.current.scrollIntoView({ behavior: 'smooth' });
    }
  }, []);
  
  useEffect(() => {
    scrollToBottom();
  }, [messages, scrollToBottom]);
  
  // Load conversations on mount
  useEffect(() => {
    if (isOpen) {
      loadConversations();
    }
  }, [isOpen]);
  
  const loadConversations = async () => {
    setIsLoadingConversations(true);
    try {
      const data = await getConversations();
      setConversations(data.results || []);
    } catch (err) {
      console.error('Failed to load conversations:', err);
    } finally {
      setIsLoadingConversations(false);
    }
  };
  
  const loadConversationMessages = async (conversationId: number) => {
    setIsLoading(true);
    try {
      const data = await getConversationMessages(conversationId);
      setMessages(data.results || []);
      setActiveConversationId(conversationId);
      setShowHistory(false);
      setError(null);
    } catch (err) {
      console.error('Failed to load messages:', err);
      toast({
        title: 'Error',
        description: 'Failed to load conversation messages',
        variant: 'destructive',
      });
    } finally {
      setIsLoading(false);
    }
  };
  
  const handleNewConversation = () => {
    setMessages([]);
    setActiveConversationId(null);
    setShowHistory(false);
    setError(null);
    setInputValue('');
  };
  
  const handleArchiveConversation = async (id: number) => {
    try {
      await archiveConversation(id);
      setConversations((prev) => prev.filter((c) => c.id !== id));
      if (activeConversationId === id) {
        handleNewConversation();
      }
      toast({
        title: 'Conversation archived',
        description: 'The conversation has been archived successfully.',
      });
    } catch (err) {
      console.error('Failed to archive conversation:', err);
      toast({
        title: 'Error',
        description: 'Failed to archive conversation',
        variant: 'destructive',
      });
    }
  };
  
  const handleSendMessage = async () => {
    if (!inputValue.trim() || isLoading) return;
    
    const userMessage: AIMessage = {
      id: Date.now(),
      conversation: activeConversationId || 0,
      role: 'user',
      content: inputValue.trim(),
      created_at: new Date().toISOString(),
    };
    
    setMessages((prev) => [...prev, userMessage]);
    setInputValue('');
    setIsLoading(true);
    setActivity('Thinking…');
    setError(null);

    const request = {
      message: userMessage.content,
      conversation_id: activeConversationId,
      attachment_ids: attachments.length ? attachments.map((a) => a.id) : undefined,
    };
    setAttachments([]);
    const placeholderId = Date.now() + 1;
    let streamedText = '';

    const upsertPlaceholder = (content: string) => {
      setMessages((prev) => {
        if (prev.some((m) => m.id === placeholderId)) {
          return prev.map((m) => (m.id === placeholderId ? { ...m, content } : m));
        }
        return [
          ...prev,
          {
            id: placeholderId,
            conversation: activeConversationId || 0,
            role: 'assistant' as const,
            content,
            created_at: new Date().toISOString(),
          },
        ];
      });
    };

    try {
      let response: AIChatResponse;
      try {
        response = await streamChatMessage(request, {
          onDelta: (text) => {
            streamedText += text;
            setActivity(null); // text is visibly streaming
            upsertPlaceholder(streamedText);
          },
          onTool: (tool, detail) => setActivity(toolActivityLabel(tool, detail)),
          onPing: () => setActivity((current) => current ?? 'Thinking…'),
          onModel: (model) =>
            setActivity(
              model.includes('opus')
                ? 'Complex task — switching to the advanced model…'
                : 'Thinking…',
            ),
        });
      } catch (err: any) {
        if (err?.code === 'NO_STREAM') {
          // Backend without the streaming endpoint yet — use the blocking one
          response = await sendChatMessage(request);
        } else {
          throw err;
        }
      }

      // Update the conversation ID if this was a new conversation
      if (!activeConversationId && response.conversation_id) {
        setActiveConversationId(response.conversation_id);
        loadConversations(); // Refresh the conversation list
      }

      const assistantMessage: AIMessage = {
        id: placeholderId,
        conversation: response.conversation_id,
        role: 'assistant',
        content: response.message,
        created_at: new Date().toISOString(),
        tool_calls: response.tool_calls,
        tokens_used: response.tokens_used,
      };

      setMessages((prev) => [
        ...prev.filter((m) => m.id !== placeholderId),
        assistantMessage,
      ]);
    } catch (err: any) {
      console.error('Failed to send message:', err);
      // Drop any partial streamed message
      setMessages((prev) => prev.filter((m) => m.id !== placeholderId));
      // A network drop mid-stream: the server keeps generating and saves the
      // answer, so point the user at the conversation history.
      const networkFailure = err instanceof TypeError;
      const interruptedMessage = streamedText
        ? 'The connection dropped mid-response. The assistant finishes in the background — reopen this conversation in a moment to see the full answer.'
        : 'Failed to get a response from the AI assistant';
      const errorMessage =
        err.response?.data?.detail ||
        err.response?.data?.error ||
        (networkFailure ? interruptedMessage : err.message || interruptedMessage);
      setError(errorMessage);
      toast({
        title: 'AI Error',
        description: errorMessage,
        variant: 'destructive',
      });
    } finally {
      setIsLoading(false);
      setActivity(null);
    }
  };
  
  const handleSuggestionClick = (suggestion: string) => {
    setInputValue(suggestion);
  };

  const handleAttach = async (file: File) => {
    setIsUploading(true);
    try {
      const attachment = await uploadAIAttachment(file);
      setAttachments((prev) => [...prev, attachment]);
    } catch (err: any) {
      console.error('Failed to upload attachment:', err);
      toast({
        title: 'Upload failed',
        description: err.response?.data?.error || 'Could not upload that file',
        variant: 'destructive',
      });
    } finally {
      setIsUploading(false);
    }
  };
  
  const userName = user ? `${user.first_name} ${user.last_name}`.trim() || 'You' : 'You';
  
  if (!isOpen) return null;

  return (
    <div
      className={cn(
        'flex flex-col bg-background',
        variant === 'drawer'
          ? 'fixed inset-y-0 right-0 w-full sm:w-[480px] md:w-[560px] lg:w-[640px] z-50 border-l border-border animate-in slide-in-from-right duration-300'
          : 'min-h-0 flex-1 overflow-hidden rounded-2xl bg-card',
      )}
    >
      {/* Header */}
      <div className="flex items-center justify-between px-4 py-2 border-b">
        <h2 className="font-semibold">Sentra AI</h2>
        
        <div className="flex items-center gap-1">
          <TooltipProvider>
            <Tooltip>
              <TooltipTrigger asChild>
                <Button
                  variant="outline"
                  size="icon"
                  onClick={() => setShowHistory(!showHistory)}
                  className={cn(showHistory && 'bg-muted')}
                >
                  <History className="h-4 w-4" />
                </Button>
              </TooltipTrigger>
              <TooltipContent>
                <p>Conversation history</p>
              </TooltipContent>
            </Tooltip>
          </TooltipProvider>
          
          <TooltipProvider>
            <Tooltip>
              <TooltipTrigger asChild>
                <Button variant="outline" size="icon" onClick={handleNewConversation}>
                  <RefreshCw className="h-4 w-4" />
                </Button>
              </TooltipTrigger>
              <TooltipContent>
                <p>New conversation</p>
              </TooltipContent>
            </Tooltip>
          </TooltipProvider>
          
          {variant === 'drawer' && (
            <Button variant="outline" size="icon" onClick={onClose}>
              <X className="h-4 w-4" />
            </Button>
          )}
        </div>
      </div>
      
      {/* Content */}
      <div className="flex-1 flex overflow-hidden">
        {/* Conversation History Sidebar */}
        {showHistory && (
          <div className="w-64 shrink-0 border-r">
            <AIConversationList
              conversations={conversations}
              activeConversationId={activeConversationId}
              onSelectConversation={loadConversationMessages}
              onNewConversation={handleNewConversation}
              onArchiveConversation={handleArchiveConversation}
              isLoading={isLoadingConversations}
            />
          </div>
        )}
        
        {/* Chat Area */}
        <div className="flex-1 flex flex-col min-w-0">
          <ScrollArea className="flex-1 px-6" ref={scrollAreaRef}>
            {messages.length === 0 ? (
              /* Welcome Screen */
              <div className="flex flex-col items-center justify-center h-full py-8 px-4">
                <h3 className="text-xl font-semibold mb-2">Welcome to Sentra AI</h3>
                <p className="text-sm text-muted-foreground text-center mb-6 max-w-sm">
                  I can help you manage your legal practice. Ask me about clients, matters, 
                  timesheets, invoices, and more.
                </p>
                
                <p className="text-xs text-muted-foreground text-center mb-2">Try asking:</p>
                <div className="grid w-full max-w-2xl gap-2 sm:grid-cols-2">
                  {WELCOME_SUGGESTIONS.map((suggestion, index) => (
                    <button
                      key={index}
                      onClick={() => handleSuggestionClick(suggestion)}
                      className="w-full text-left px-4 py-2.5 rounded-lg border bg-card hover:bg-accent hover:border-ring/40 transition-colors text-sm"
                    >
                      {suggestion}
                    </button>
                  ))}
                </div>
                
                <div className="mt-6 flex items-start gap-2 text-xs text-muted-foreground max-w-2xl">
                  <Info className="h-4 w-4 shrink-0 mt-0.5" />
                  <p>
                    All operations use your permissions. The AI can only access data you're authorized to see.
                  </p>
                </div>
              </div>
            ) : (
              /* Messages */
              <div className={cn('py-4 space-y-1', variant === 'page' && 'mx-auto w-full max-w-5xl')}>
                {messages.map((message) => (
                  <AIMessageItem
                    key={message.id}
                    message={message}
                    userName={userName}
                  />
                ))}
                
                {isLoading && activity && (
                  <div className="flex items-center gap-3 py-4">
                    <div className="flex items-center gap-1.5">
                      <div className="h-2 w-2 rounded-full bg-primary animate-pulse [animation-delay:-0.3s]" />
                      <div className="h-2 w-2 rounded-full bg-primary animate-pulse [animation-delay:-0.15s]" />
                      <div className="h-2 w-2 rounded-full bg-primary animate-pulse" />
                    </div>
                    <span className="text-xs text-muted-foreground animate-pulse">{activity}</span>
                  </div>
                )}
                
                {error && (
                  <div className="flex items-start gap-2 text-sm text-destructive bg-destructive/10 px-4 py-3 rounded-lg">
                    <Info className="h-4 w-4 shrink-0 mt-0.5" />
                    <div>
                      <p className="font-medium">Error</p>
                      <p className="text-xs mt-1">{error}</p>
                    </div>
                  </div>
                )}
                
                <div ref={messagesEndRef} />
              </div>
            )}
          </ScrollArea>
          
          {/* Input */}
          <div className={cn(variant === 'page' && 'mx-auto w-full max-w-5xl')}>
            <AIChatInput
              value={inputValue}
              onChange={setInputValue}
              onSend={handleSendMessage}
              isLoading={isLoading}
              className={variant === 'page' ? 'border-t-0 bg-transparent' : undefined}
              attachments={attachments}
              isUploading={isUploading}
              onAttach={handleAttach}
              onRemoveAttachment={(id) =>
                setAttachments((prev) => prev.filter((a) => a.id !== id))
              }
            />
          </div>
        </div>
      </div>
    </div>
  );
}
